import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, Tabs, Tab } from '@mui/material';
import { derivePersonalBests } from '../../utils/testStats';
import { unitLabel } from '../../utils/testUnits';
import TestAttemptCapture from './TestAttemptCapture';

const MEASUREMENT_METHOD_LABEL = { manual: 'Own Stopwatch', app_stopwatch: 'FFN Stopwatch' };

// Testing Occurrence / Attempt Protocol (059) — mirrors TeamTestingStation
// .js's own occurrenceKey() exactly; Personal Testing has only one
// athlete (the `athlete` prop, already known), so this is keyed only by
// test type.
function occurrenceKey(testTypeKey) {
  return testTypeKey;
}

// PERSONAL TESTING STATION — the guardian-operated counterpart to
// TeamTestingStation.js, sharing its capture/feedback experience via
// TestAttemptCapture (test tabs, the measurement-method gate, and the
// stopwatch state machine itself are intentionally NOT extracted —
// each of those is small, fully controlled by this component's own
// state, and the Personal/Team persistence-across-attempts semantics
// differ enough that duplicating this thin layer is safer than a
// forced shared abstraction — see the Personal Testing Station
// architecture audit).
//
// The Athlete is ALREADY KNOWN (passed in as a prop) — there is no
// roster lookup, no jersey/name search, and no "Correct athlete?"
// confirmation anywhere in this component, by construction: it has no
// roster prop at all, and nothing here can even ask for one.
//
// Deliberately no react-router-dom import — router-free, props/
// callbacks only (athlete, testTypes, fetchAthleteSessions,
// recordAttempt), same testability discipline as TeamTestingStation.js.
export default function PersonalTestingStation({
  athlete, testTypes, fetchAthleteSessions, recordAttempt,
  // Testing Occurrence / Attempt Protocol (059) — optional/backward-
  // compatible: a caller that omits it simply never renders End Test,
  // and capture behaves exactly as it always has. No Change Max/Reopen
  // props exist here at all — Personal Testing never gets that
  // privilege in V1 (059's own server-side authorization independently
  // enforces this regardless of what this component does or doesn't
  // render).
  onEndTestEarly,
}) {
  const [testTypeKey, setTestTypeKey] = useState(testTypes[0]?.key);
  const [measurementMethodByTestKey, setMeasurementMethodByTestKey] = useState({});
  const [phase, setPhase] = useState('capture'); // 'capture' | 'feedback'
  const [stopwatchPhase, setStopwatchPhase] = useState('ready'); // 'ready' | 'running' | 'captured'
  const [stopwatchStartedAt, setStopwatchStartedAt] = useState(null);
  const [candidateResult, setCandidateResult] = useState(null);
  const [resultValue, setResultValue] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // ONE session id for this entire station visit, regardless of which
  // test is active — exactly mirroring how a Team Testing occurrence
  // (049) already accumulates multiple DIFFERENT test types into the
  // same testing_sessions row; attempt_number is independently scoped
  // per test_type within it, server-side.
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  // Testing Occurrence / Attempt Protocol (059) — cached, server-
  // confirmed occurrence state per test type, same in-memory-only
  // lifetime as sessionId above. Absent until the first attempt of this
  // visit for that test type returns a real occurrence_id.
  const [occurrenceByKey, setOccurrenceByKey] = useState({});

  const activeTestType = testTypes.find(t => t.key === testTypeKey) || testTypes[0];
  // Custom Test Types (056) — see TeamTestingStation.js's identical
  // comment: supports_app_stopwatch is now an explicit, independent
  // fact about the test, never inferred from unit === 'seconds' alone;
  // the `&& unit === 'seconds'` here is a redundant, defense-in-depth
  // mirror of the server-side CHECK that actually enforces it.
  const supportsStopwatch = Boolean(activeTestType?.supports_app_stopwatch) && activeTestType?.unit === 'seconds';
  const currentMethod = supportsStopwatch ? measurementMethodByTestKey[activeTestType.key] : 'manual';
  const needsMethodSelection = supportsStopwatch && !currentMethod;
  const isAppStopwatch = supportsStopwatch && currentMethod === 'app_stopwatch';

  const refreshSessions = () => fetchAthleteSessions(athlete.id)
    .then(s => { setSessions(s); setSessionsLoaded(true); })
    .catch(() => setSessionsLoaded(true));

  // Loaded once for the athlete's ENTIRE history — Career PB, same as
  // DrillHistory.js's own derivePersonalBests() usage, never re-fetched
  // just because the operator switched test tabs.
  useEffect(() => {
    let cancelled = false;
    fetchAthleteSessions(athlete.id)
      .then(s => { if (!cancelled) setSessions(s); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setSessionsLoaded(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athlete.id]);

  const personalBests = useMemo(() => derivePersonalBests(sessions, testTypes), [sessions, testTypes]);
  const previousPb = sessionsLoaded ? (personalBests.find(p => p.testType.key === activeTestType?.key) || null) : undefined;

  const resetCapture = () => {
    setPhase('capture');
    setResultValue('');
    setError(null);
    setFeedback(null);
    setStopwatchPhase('ready');
    setStopwatchStartedAt(null);
    setCandidateResult(null);
  };

  const handleTestTypeChange = (key) => {
    setTestTypeKey(key);
    resetCapture(); // a mid-flow test change always aborts the current in-progress attempt
  };

  const handleSelectMethod = (method) => {
    setMeasurementMethodByTestKey(prev => ({ ...prev, [activeTestType.key]: method }));
  };

  const handleChangeMethod = () => {
    setMeasurementMethodByTestKey(prev => {
      const next = { ...prev };
      delete next[activeTestType.key];
      return next;
    });
  };

  const submitAttempt = async (rawValue, captureMethod) => {
    setError(null);
    const parsed = Number(rawValue);
    if (rawValue === '' || rawValue === null || !Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter a positive number.');
      return;
    }
    setSaving(true);
    try {
      const result = await recordAttempt({
        athleteId: athlete.id,
        testType: activeTestType.key,
        resultValue: parsed,
        testDate: new Date().toISOString().slice(0, 10),
        sessionId,
        captureMethod,
      });
      setSessionId(result.session_id);
      // Testing Occurrence / Attempt Protocol (059) — see
      // TeamTestingStation.js's identical comment: absent on a mocked/
      // legacy recordAttempt response, in which case this is a no-op.
      if (result.occurrence_id) {
        setOccurrenceByKey(prev => ({
          ...prev,
          [occurrenceKey(activeTestType.key)]: {
            occurrenceId: result.occurrence_id,
            status: result.occurrence_status,
            validAttemptCount: result.valid_attempt_count,
            plannedMaxAttempts: result.planned_max_attempts,
          },
        }));
      }

      const isNewPb = previousPb
        ? (activeTestType.better_direction === 'lower' ? parsed < previousPb.value : parsed > previousPb.value)
        : true;
      setFeedback({ value: parsed, isNewPb, unit: unitLabel(activeTestType.unit) });
      setPhase('feedback');
      refreshSessions(); // so an immediate next attempt compares against the just-set PB
    } catch (e) {
      setError(e.message || 'Could not record this attempt.');
    } finally {
      setSaving(false);
    }
  };

  const handleManualRecord = () => submitAttempt(resultValue, 'manual');

  // Testing Occurrence / Attempt Protocol (059) — the athlete/guardian's
  // own "I'm done" action. No reason picker (endTestReasonOptions is
  // never passed to TestAttemptCapture below) — a guardian ending their
  // own athlete's test isn't filling out a coach's structured form.
  const handleEndTest = async (occId, { note }) => {
    const updated = await onEndTestEarly({ occurrenceId: occId, note });
    setOccurrenceByKey(prev => ({
      ...prev,
      [occurrenceKey(activeTestType.key)]: { ...prev[occurrenceKey(activeTestType.key)], status: updated.status },
    }));
  };

  const handleStopwatchStart = () => {
    setError(null);
    setStopwatchStartedAt(performance.now());
    setStopwatchPhase('running');
  };
  const handleStopwatchStop = () => {
    const elapsedSeconds = (performance.now() - stopwatchStartedAt) / 1000;
    setCandidateResult(Math.round(elapsedSeconds * 100) / 100);
    setStopwatchPhase('captured');
  };
  const handleStopwatchRetry = () => {
    setCandidateResult(null);
    setStopwatchStartedAt(null);
    setStopwatchPhase('ready');
  };
  const handleStopwatchRecord = () => submitAttempt(candidateResult, 'app_stopwatch');

  // Cancel and Test Again both return to a clean, ready-to-test state
  // for the SAME athlete and the SAME test/method — the athlete is
  // never re-asked for, since there was never a lookup step in the
  // first place.
  const handleCancel = () => resetCapture();
  const handleTestAgain = () => resetCapture();

  if (!activeTestType) {
    return <Typography sx={{ color: '#6a8aa8' }}>No test types available.</Typography>;
  }

  const resultUnitLabel = unitLabel(activeTestType.unit);

  return (
    <Box>
      <Tabs
        value={activeTestType.key}
        onChange={(e, v) => handleTestTypeChange(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: '1px solid #1e2a3a', minHeight: 48,
          '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 800, fontSize: '0.95rem', px: 2.5 } }}
      >
        {testTypes.map(t => <Tab key={t.key} value={t.key} label={t.display_name} />)}
      </Tabs>

      {supportsStopwatch && currentMethod && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem' }}>
            Measurement: <strong>{MEASUREMENT_METHOD_LABEL[currentMethod]}</strong>
          </Typography>
          {phase === 'capture' && stopwatchPhase === 'ready' && (
            <Button size="small" onClick={handleChangeMethod} sx={{ textTransform: 'none', color: '#6a8aa8', minWidth: 0, p: 0 }}>
              Change
            </Button>
          )}
        </Box>
      )}

      {needsMethodSelection ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', mb: 3 }}>How will this data be measured?</Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => handleSelectMethod('app_stopwatch')} variant="contained" color="primary" size="large"
              sx={{ textTransform: 'none', fontWeight: 800, px: 4, py: 2, fontSize: '1.05rem' }}
            >
              USE FFN STOPWATCH
            </Button>
            <Button
              onClick={() => handleSelectMethod('manual')} variant="outlined" color="primary" size="large"
              sx={{ textTransform: 'none', fontWeight: 800, px: 4, py: 2, fontSize: '1.05rem' }}
            >
              I HAVE MY OWN STOPWATCH
            </Button>
          </Box>
        </Box>
      ) : phase === 'capture' ? (
        <TestAttemptCapture
          phase="armed"
          subject={{ jerseyNumber: null, firstName: athlete.first_name, lastName: athlete.last_name }}
          previousPb={previousPb}
          unitLabel={resultUnitLabel}
          isAppStopwatch={isAppStopwatch}
          stopwatchPhase={stopwatchPhase}
          stopwatchStartedAt={stopwatchStartedAt}
          candidateResult={candidateResult}
          resultValue={resultValue}
          onResultChange={setResultValue}
          error={error}
          saving={saving}
          onStopwatchStart={handleStopwatchStart}
          onStopwatchStop={handleStopwatchStop}
          onStopwatchRetry={handleStopwatchRetry}
          onStopwatchRecord={handleStopwatchRecord}
          onManualRecord={handleManualRecord}
          onCancel={handleCancel}
          occurrence={occurrenceByKey[occurrenceKey(activeTestType.key)]}
          onEndTest={onEndTestEarly ? handleEndTest : undefined}
        />
      ) : (
        <TestAttemptCapture
          phase="feedback"
          subject={{ jerseyNumber: null, firstName: athlete.first_name, lastName: athlete.last_name }}
          previousPb={previousPb}
          feedback={feedback}
          nextActionLabel="Test Again"
          onNext={handleTestAgain}
          occurrence={occurrenceByKey[occurrenceKey(activeTestType.key)]}
          onEndTest={onEndTestEarly ? handleEndTest : undefined}
          // BUGFIX (059) — "Test Again" literally means "record another
          // attempt for this same occurrence right now," which is
          // contradictory once it has completed (no Change Max/Reopen
          // exists in Personal V1 to make another attempt possible
          // anyway). See TestAttemptCapture.js's own prop comment for
          // why this is Personal-only.
          hideNextWhenCompleted
        />
      )}
    </Box>
  );
}
